import { useEffect, useMemo, useRef } from "react"

import { useTranslation } from "react-i18next"

import type { RealtimeVoiceToolApproval } from "../hooks/use-realtime-audio"

import { Calendar, Check, Clock, Loader2, Mail, ShieldCheck, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { useApprovalCountdown } from "@/features/chat/hooks/use-approval-countdown"
import { cn } from "@/lib/utils"

type VoiceToolApprovalCardProps = {
  approval: RealtimeVoiceToolApproval
  submitting: boolean
  position: number
  total: number
  onApprove: () => void
  onReject: () => void
}

type ApprovalDetail = {
  label: string
  value: string
}

type ApprovalLabels = {
  recipients: string
  cc: string
  bcc: string
  subject: string
  body: string
  bodyType: string
  saveToSent: string
  start: string
  end: string
  timezone: string
  location: string
  attendees: string
  onlineMeeting: string
  allDay: string
  sensitivity: string
  availability: string
  importance: string
  categories: string
  reminder: string
  event: string
  yes: string
  no: string
  none: string
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null
}

function formatList(value: unknown): string | null {
  if (!Array.isArray(value)) return null
  const entries = value.flatMap((item): string[] => {
    if (typeof item === "string" && item.trim()) return [item.trim()]
    const record = asRecord(item)
    if (!record) return []
    const emailAddress = asRecord(record.emailAddress)
    const label = record.email ?? record.address ?? emailAddress?.address ?? record.name
    return typeof label === "string" && label.trim() ? [label.trim()] : []
  })
  return entries.length > 0 ? entries.join(", ") : null
}

function formatText(value: unknown): string | null {
  const content = typeof value === "string" ? value : asRecord(value)?.content
  if (typeof content !== "string" || !content.trim()) return null
  const normalized = content.replace(/\s+/g, " ").trim()
  return normalized.length > 500 ? `${normalized.slice(0, 500)}…` : normalized
}

function formatLocation(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value.trim()
  const displayName = asRecord(value)?.displayName
  return typeof displayName === "string" && displayName.trim() ? displayName.trim() : null
}

function formatScalar(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value.trim()
  return typeof value === "number" && Number.isFinite(value) ? String(value) : null
}

function formatDateTime(value: unknown): string | null {
  const raw = typeof value === "string" ? value : asRecord(value)?.dateTime
  if (typeof raw !== "string" || !raw) return null
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return raw
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date)
}

function addDetail(details: ApprovalDetail[], label: string, value: string | null) {
  if (value) details.push({ label, value })
}

function formatChange(
  [originalValue, nextValue, hasNextValue]: readonly [unknown, unknown, boolean],
  formatter: (value: unknown) => string | null,
  noneLabel: string
): string | null {
  if (!hasNextValue) return null
  const before = formatter(originalValue)
  const after = formatter(nextValue) ?? noneLabel
  return before && before !== after ? `${before} → ${after}` : after
}

function buildDetails(
  approval: RealtimeVoiceToolApproval,
  labels: ApprovalLabels
): ApprovalDetail[] {
  const action = typeof approval.arguments.action === "string" ? approval.arguments.action : ""
  const parameters = asRecord(approval.arguments.parameters) ?? approval.arguments
  const details: ApprovalDetail[] = []

  if (action === "send_email") {
    addDetail(details, labels.recipients, formatList(parameters.to))
    addDetail(details, labels.cc, formatList(parameters.cc))
    addDetail(details, labels.bcc, formatList(parameters.bcc))
    addDetail(details, labels.subject, formatText(parameters.subject))
    addDetail(details, labels.body, formatText(parameters.body))
    addDetail(details, labels.bodyType, formatScalar(parameters.body_type) ?? "html")
    addDetail(
      details,
      labels.saveToSent,
      parameters.save_to_sent === false ? labels.no : labels.yes
    )
    return details
  }

  if (action === "create_calendar_event") {
    const startRecord = asRecord(parameters.start)
    const endRecord = asRecord(parameters.end)
    addDetail(details, labels.subject, formatText(parameters.subject))
    addDetail(details, labels.start, formatDateTime(parameters.start))
    addDetail(details, labels.end, formatDateTime(parameters.end))
    addDetail(
      details,
      labels.timezone,
      formatText(parameters.timezone ?? startRecord?.timeZone ?? endRecord?.timeZone ?? "UTC")
    )
    addDetail(details, labels.location, formatLocation(parameters.location))
    addDetail(details, labels.attendees, formatList(parameters.attendees))
    addDetail(
      details,
      labels.onlineMeeting,
      (parameters.is_online_meeting ?? parameters.isOnlineMeeting) === true ? labels.yes : labels.no
    )
    addDetail(details, labels.body, formatText(parameters.body))
    addDetail(details, labels.bodyType, formatScalar(parameters.body_type))
    return details
  }

  if (action === "edit_calendar_event") {
    const original = asRecord(parameters._original) ?? {}
    addDetail(
      details,
      labels.event,
      formatText(parameters.event_name ?? parameters.event_id ?? original.subject)
    )
    addDetail(
      details,
      labels.subject,
      formatChange(
        [original.subject, parameters.subject, Object.hasOwn(parameters, "subject")],
        formatText,
        labels.none
      )
    )
    addDetail(
      details,
      labels.start,
      formatChange(
        [original.start, parameters.start, Object.hasOwn(parameters, "start")],
        formatDateTime,
        labels.none
      )
    )
    addDetail(
      details,
      labels.end,
      formatChange(
        [original.end, parameters.end, Object.hasOwn(parameters, "end")],
        formatDateTime,
        labels.none
      )
    )
    addDetail(
      details,
      labels.timezone,
      formatChange(
        [original.timezone, parameters.timezone, Object.hasOwn(parameters, "timezone")],
        formatText,
        labels.none
      )
    )
    addDetail(
      details,
      labels.location,
      formatChange(
        [original.location, parameters.location, Object.hasOwn(parameters, "location")],
        formatLocation,
        labels.none
      )
    )
    addDetail(
      details,
      labels.attendees,
      formatChange(
        [original.attendees, parameters.attendees, Object.hasOwn(parameters, "attendees")],
        formatList,
        labels.none
      )
    )
    const hasOnlineMeeting =
      Object.hasOwn(parameters, "is_online_meeting") || Object.hasOwn(parameters, "isOnlineMeeting")
    const originalOnlineMeeting = original.is_online_meeting ?? original.isOnlineMeeting
    const nextOnlineMeeting = parameters.is_online_meeting ?? parameters.isOnlineMeeting
    addDetail(
      details,
      labels.onlineMeeting,
      formatChange(
        [originalOnlineMeeting, nextOnlineMeeting, hasOnlineMeeting],
        (value) => (value === true ? labels.yes : labels.no),
        labels.none
      )
    )
    addDetail(
      details,
      labels.body,
      formatChange(
        [original.body, parameters.body, Object.hasOwn(parameters, "body")],
        formatText,
        labels.none
      )
    )
    addDetail(
      details,
      labels.allDay,
      formatChange(
        [original.isAllDay, parameters.isAllDay, Object.hasOwn(parameters, "isAllDay")],
        (value) => (value === true ? labels.yes : labels.no),
        labels.none
      )
    )
    addDetail(
      details,
      labels.sensitivity,
      formatChange(
        [original.sensitivity, parameters.sensitivity, Object.hasOwn(parameters, "sensitivity")],
        formatScalar,
        labels.none
      )
    )
    addDetail(
      details,
      labels.availability,
      formatChange(
        [original.showAs, parameters.showAs, Object.hasOwn(parameters, "showAs")],
        formatScalar,
        labels.none
      )
    )
    addDetail(
      details,
      labels.importance,
      formatChange(
        [original.importance, parameters.importance, Object.hasOwn(parameters, "importance")],
        formatScalar,
        labels.none
      )
    )
    addDetail(
      details,
      labels.categories,
      formatChange(
        [original.categories, parameters.categories, Object.hasOwn(parameters, "categories")],
        formatList,
        labels.none
      )
    )
    addDetail(
      details,
      labels.reminder,
      formatChange(
        [
          original.reminderMinutesBeforeStart,
          parameters.reminderMinutesBeforeStart,
          Object.hasOwn(parameters, "reminderMinutesBeforeStart"),
        ],
        formatScalar,
        labels.none
      )
    )
  }

  return details
}

function ApprovalIcon({ action }: { action: string }) {
  if (action === "send_email") return <Mail aria-hidden="true" className="size-5" />
  if (action.includes("calendar_event")) return <Calendar aria-hidden="true" className="size-5" />
  return <ShieldCheck aria-hidden="true" className="size-5" />
}

export function VoiceToolApprovalCard({
  approval,
  submitting,
  position,
  total,
  onApprove,
  onReject,
}: VoiceToolApprovalCardProps) {
  const { t } = useTranslation()
  const cardRef = useRef<HTMLElement>(null)
  const action = typeof approval.arguments.action === "string" ? approval.arguments.action : ""
  const details = useMemo(
    () =>
      buildDetails(approval, {
        recipients: t("voice_mode.approval.recipients"),
        cc: t("voice_mode.approval.cc"),
        bcc: t("voice_mode.approval.bcc"),
        subject: t("voice_mode.approval.subject"),
        body: t("voice_mode.approval.body"),
        bodyType: t("voice_mode.approval.body_type"),
        saveToSent: t("voice_mode.approval.save_to_sent"),
        start: t("voice_mode.approval.start"),
        end: t("voice_mode.approval.end"),
        timezone: t("voice_mode.approval.timezone"),
        location: t("voice_mode.approval.location"),
        attendees: t("voice_mode.approval.attendees"),
        onlineMeeting: t("voice_mode.approval.online_meeting"),
        allDay: t("voice_mode.approval.all_day"),
        sensitivity: t("voice_mode.approval.sensitivity"),
        availability: t("voice_mode.approval.availability"),
        importance: t("voice_mode.approval.importance"),
        categories: t("voice_mode.approval.categories"),
        reminder: t("voice_mode.approval.reminder"),
        event: t("voice_mode.approval.event"),
        yes: t("voice_mode.approval.yes"),
        no: t("voice_mode.approval.no"),
        none: t("voice_mode.approval.none"),
      }),
    [approval, t]
  )
  const countdown = useApprovalCountdown({
    createdAt: approval.created_at,
    timeoutSecs: approval.timeout_secs,
    status: approval.status,
  })

  useEffect(() => {
    // Announce the request without putting the destructive approval action one
    // incidental Enter press away. The user must explicitly choose a button.
    const animationFrame = window.requestAnimationFrame(() => cardRef.current?.focus())
    return () => window.cancelAnimationFrame(animationFrame)
  }, [approval.request_id])

  return (
    <section
      ref={cardRef}
      aria-busy={submitting}
      aria-labelledby="voice-tool-approval-title"
      aria-live="assertive"
      className="border-warning/40 bg-card/95 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-2 relative z-10 mb-5 w-full max-w-sm overflow-hidden rounded-xl border shadow-xl backdrop-blur-md"
      data-testid="voice-tool-approval"
      tabIndex={-1}
    >
      <div className="flex items-start gap-3 p-4 pb-3">
        <div className="bg-warning/10 text-warning-foreground grid size-10 shrink-0 place-items-center rounded-md">
          <ApprovalIcon action={action} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3
              className="font-KHTeka-Regular text-foreground text-base leading-tight font-semibold"
              id="voice-tool-approval-title"
            >
              {t("voice_mode.approval.title")}
            </h3>
            <div className="flex shrink-0 items-center gap-2">
              {total > 1 && (
                <span className="bg-muted text-muted-foreground rounded-md px-1.5 py-0.5 text-xs font-semibold tabular-nums">
                  {position}/{total}
                </span>
              )}
              {countdown && (
                <span
                  className={cn(
                    "text-muted-foreground flex items-center gap-1 text-xs tabular-nums",
                    countdown.isExpiring && "text-destructive"
                  )}
                >
                  <Clock aria-hidden="true" className="size-3.5" />
                  {countdown.remainingSecs}s
                </span>
              )}
            </div>
          </div>
          <p className="text-muted-foreground mt-1 text-sm leading-snug">{approval.summary}</p>
        </div>
      </div>

      {details.length > 0 && (
        <dl className="border-border/60 mx-4 max-h-36 space-y-1.5 overflow-y-auto border-t pt-3 pr-1 pb-1 text-sm">
          {details.map((detail) => (
            <div key={detail.label} className="grid grid-cols-[5rem_minmax(0,1fr)] gap-2">
              <dt className="text-muted-foreground">{detail.label}</dt>
              <dd className="text-foreground min-w-0 font-medium break-words whitespace-pre-wrap">
                {detail.value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <p className="text-muted-foreground px-4 pt-2 text-center text-xs">
        {t("voice_mode.approval.spoken_hint")}
      </p>
      <div className="grid grid-cols-2 gap-2 p-4 pt-3">
        <Button className="min-h-11" disabled={submitting} type="button" onClick={onApprove}>
          {submitting ? (
            <Loader2
              aria-hidden="true"
              className="size-4 animate-spin motion-reduce:animate-none"
            />
          ) : (
            <Check aria-hidden="true" className="size-4" />
          )}
          {submitting ? t("voice_mode.approval.submitting") : t("voice_mode.approval.approve")}
        </Button>
        <Button
          className="min-h-11"
          disabled={submitting}
          type="button"
          variant="outline"
          onClick={onReject}
        >
          <X aria-hidden="true" className="size-4" />
          {t("voice_mode.approval.reject")}
        </Button>
      </div>
    </section>
  )
}
